import React, { useState } from 'react';
import { 
  // Navigation & UI
  Menu, X, Bell, Search, ChevronRight, ChevronDown, ChevronUp, MoreHorizontal,
  // Auth
  Shield, Fingerprint, Key, Mail, Lock,
  // Main modules
  LayoutDashboard, Users, Briefcase, Clock, DollarSign, FileText, CreditCard,
  Building2, HardHat, Calculator, Settings, TrendingUp, TrendingDown,
  // Actions
  Plus, Edit3, Trash2, Upload, Download, Send, Check, AlertCircle, Eye, EyeOff,
  Camera, Scan, Receipt, Link2, RefreshCw, Copy, ExternalLink, Filter,
  // Status & indicators
  CheckCircle2, AlertTriangle, Info, Loader2, Circle, ArrowRight, ArrowUpRight,
  // Features
  Mic, Brain, Sparkles, Zap, MapPin, Calendar, FileUp, Globe, Phone,
  // Specific
  Landmark, Wallet, Building, Truck, Award, GraduationCap, ClipboardList,
  Target, BarChart3, PieChart, Layers, FolderOpen, Image, Paperclip,
  UserPlus, UserCheck, BadgeCheck, ShieldCheck, LogOut, Home
} from 'lucide-react';

// ============================================
// MASONRY OS - COMPLETE SYSTEM DEMO
// All modules, clickable navigation
// Ready to show stakeholders
// ============================================

export default function MasonryOS() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentModule, setCurrentModule] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showVoice, setShowVoice] = useState(false);
  
  // Sub-states for various modules
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState(null);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { 
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 
  }).format(amount);

  // ============================================
  // AUTH SCREEN
  // ============================================
  const AuthScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-orange-500/30">
            <Building2 className="text-white" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-white">TradeOS4Subs</h1>
          <p className="text-slate-400 mt-2">The Operating System for Trade Subcontractors</p>
        </div>

        {/* Auth Card */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl">
          <h2 className="text-xl font-bold text-slate-800 text-center mb-6">Sign in to continue</h2>
          
          {/* Google Auth */}
          <button 
            onClick={() => setIsAuthenticated(true)}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl font-medium text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition-all mb-4"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Passkey Option */}
          <button 
            onClick={() => setIsAuthenticated(true)}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-slate-900 rounded-2xl font-medium text-white hover:bg-slate-800 transition-all mb-6"
          >
            <Fingerprint size={20} />
            Sign in with Passkey
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">or</span>
            </div>
          </div>

          {/* Email Magic Link */}
          <div className="space-y-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-4 py-3 rounded-xl bg-slate-100 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button 
              onClick={() => setIsAuthenticated(true)}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl font-medium text-white hover:shadow-lg transition-all"
            >
              <Mail size={18} />
              Send Login Link
            </button>
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            No password needed. We'll email you a secure link.
          </p>
        </div>

        {/* Security Badge */}
        <div className="flex items-center justify-center gap-2 mt-6 text-slate-400">
          <ShieldCheck size={16} />
          <span className="text-sm">256-bit encryption • SOC 2 compliant</span>
        </div>
      </div>
    </div>
  );

  // ============================================
  // SIDEBAR NAVIGATION
  // ============================================
  const Sidebar = () => {
    const modules = [
      { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
      { id: 'jobs', name: 'Jobs', icon: Briefcase, badge: 6 },
      { id: 'time', name: 'Time Tracking', icon: Clock },
      { id: 'employees', name: 'Employees', icon: Users, badge: 47 },
      { id: 'payroll', name: 'Payroll', icon: DollarSign },
      { id: 'billing', name: 'Billing & SOV', icon: FileText },
      { id: 'expenses', name: 'Credit Cards', icon: CreditCard, isNew: true },
      { id: 'backlog', name: 'Backlog', icon: TrendingUp },
      { id: 'changeorders', name: 'Change Orders', icon: ClipboardList },
      { id: 'production', name: 'Production', icon: BarChart3 },
      { id: 'compliance', name: 'Compliance', icon: Shield },
      { id: 'paygroups', name: 'Pay Groups', icon: Calculator },
      { id: 'settings', name: 'Settings', icon: Settings },
    ];

    return (
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white flex flex-col transition-all duration-300`}>
        {/* Header */}
        <div className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center flex-shrink-0">
            <Building2 size={22} />
          </div>
          {sidebarOpen && (
            <div>
              <h1 className="font-bold text-lg">TradeOS</h1>
              <p className="text-xs text-slate-400">Romero Masonry</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {modules.map((module) => (
            <button
              key={module.id}
              onClick={() => setCurrentModule(module.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                currentModule === module.id
                  ? 'bg-orange-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <module.icon size={20} className="flex-shrink-0" />
              {sidebarOpen && (
                <>
                  <span className="flex-1 text-left text-sm font-medium">{module.name}</span>
                  {module.badge && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-white/20">{module.badge}</span>
                  )}
                  {module.isNew && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500">New</span>
                  )}
                </>
              )}
            </button>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
              RS
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">Roberto S.</p>
                <p className="text-xs text-slate-400">Owner</p>
              </div>
            )}
            {sidebarOpen && (
              <button 
                onClick={() => setIsAuthenticated(false)}
                className="p-2 hover:bg-slate-800 rounded-lg"
              >
                <LogOut size={16} className="text-slate-400" />
              </button>
            )}
          </div>
        </div>

        {/* Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-4 border-t border-slate-800 text-slate-400 hover:text-white flex items-center justify-center"
        >
          {sidebarOpen ? <ChevronDown size={20} className="rotate-90" /> : <ChevronDown size={20} className="-rotate-90" />}
        </button>
      </div>
    );
  };

  // ============================================
  // TOP HEADER
  // ============================================
  const Header = () => (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
      <div className="flex items-center gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search jobs, employees, invoices..."
            className="w-80 pl-10 pr-4 py-2 rounded-xl bg-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        {/* Voice Button */}
        <button
          onClick={() => setShowVoice(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium hover:shadow-lg transition-all"
        >
          <Mic size={18} />
          <span className="text-sm">Voice</span>
        </button>
        <button className="relative p-2 hover:bg-slate-100 rounded-xl">
          <Bell size={20} className="text-slate-600" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        </button>
        <button className="p-2 hover:bg-slate-100 rounded-xl">
          <Settings size={20} className="text-slate-600" />
        </button>
      </div>
    </header>
  );

  // ============================================
  // DASHBOARD MODULE
  // ============================================
  const DashboardModule = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Good morning, Roberto</h1>
          <p className="text-slate-500">Here's what's happening today</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Calendar size={16} />
          <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Active Backlog', value: '$2.4M', change: '+12%', positive: true, icon: TrendingUp },
          { label: 'Pending COs', value: '$55.5K', count: '4 pending', icon: ClipboardList },
          { label: 'This Week Payroll', value: '$47,200', subtext: '52 employees', icon: DollarSign },
          { label: 'Unbilled Work', value: '$312K', subtext: 'Ready to bill', icon: FileText },
        ].map((metric, idx) => (
          <div key={idx} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <metric.icon size={20} className="text-slate-600" />
              </div>
              {metric.change && (
                <span className={`flex items-center gap-1 text-sm font-medium ${metric.positive ? 'text-emerald-600' : 'text-red-600'}`}>
                  {metric.positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {metric.change}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-slate-800">{metric.value}</p>
            <p className="text-sm text-slate-500">{metric.subtext || metric.count || metric.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-6 gap-3">
        {[
          { label: 'Log Time', icon: Clock, color: 'from-blue-500 to-cyan-500' },
          { label: 'New CO', icon: Plus, color: 'from-orange-500 to-red-500' },
          { label: 'Add Receipt', icon: Receipt, color: 'from-emerald-500 to-teal-500' },
          { label: 'Run Payroll', icon: DollarSign, color: 'from-violet-500 to-purple-500' },
          { label: 'Create Invoice', icon: FileText, color: 'from-pink-500 to-rose-500' },
          { label: 'Scan Document', icon: Scan, color: 'from-slate-600 to-slate-700' },
        ].map((action, idx) => (
          <button key={idx} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all text-center">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mx-auto mb-2`}>
              <action.icon size={22} className="text-white" />
            </div>
            <span className="text-sm font-medium text-slate-700">{action.label}</span>
          </button>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Today's Crews */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800">Today's Crews</h3>
            <span className="text-sm text-slate-500">6 active jobs</span>
          </div>
          <div className="space-y-3">
            {[
              { job: 'CCC Sports Complex', crew: 8, foreman: 'Mike R.', status: 'On site' },
              { job: 'Riverton Hospital', crew: 6, foreman: 'Carlos M.', status: 'On site' },
              { job: 'Alliant', crew: 4, foreman: 'Jose L.', status: 'On site' },
              { job: 'Fred Meyer Fueling', crew: 3, foreman: 'David S.', status: 'Travel' },
            ].map((crew, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors">
                <div>
                  <p className="font-medium text-slate-800">{crew.job}</p>
                  <p className="text-sm text-slate-500">{crew.foreman} • {crew.crew} workers</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  crew.status === 'On site' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {crew.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800">Recent Activity</h3>
            <button className="text-sm text-orange-600 font-medium">View All</button>
          </div>
          <div className="space-y-3">
            {[
              { action: 'Change order approved', job: 'Riverton Hospital', amount: '+$15,000', time: '2 min ago', type: 'co' },
              { action: 'Invoice paid', job: 'Sheridan Holiday Inn', amount: '$42,500', time: '1 hour ago', type: 'payment' },
              { action: 'Time submitted', job: 'CCC Sports Complex', amount: '64 hours', time: '2 hours ago', type: 'time' },
              { action: 'Receipt uploaded', job: 'General', amount: '$847.50', time: '3 hours ago', type: 'expense' },
            ].map((activity, idx) => (
              <div key={idx} className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  activity.type === 'co' ? 'bg-orange-100' :
                  activity.type === 'payment' ? 'bg-emerald-100' :
                  activity.type === 'time' ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  {activity.type === 'co' && <ClipboardList size={18} className="text-orange-600" />}
                  {activity.type === 'payment' && <DollarSign size={18} className="text-emerald-600" />}
                  {activity.type === 'time' && <Clock size={18} className="text-blue-600" />}
                  {activity.type === 'expense' && <Receipt size={18} className="text-purple-600" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{activity.action}</p>
                  <p className="text-sm text-slate-500">{activity.job}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-slate-800">{activity.amount}</p>
                  <p className="text-xs text-slate-400">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // EMPLOYEES MODULE
  // ============================================
  const EmployeesModule = () => {
    const employees = [
      { id: 1, name: 'Mike Rodriguez', role: 'Foreman', payGroup: 'Foreman Bricklayer (4+)', rate: 54.10, status: 'Active', union: 'BAC Local 1', phone: '503-555-0101', hired: '2019-03-15', hours: 42 },
      { id: 2, name: 'Carlos Martinez', role: 'Foreman', payGroup: 'Foreman Bricklayer (1-3)', rate: 52.60, status: 'Active', union: 'BAC Local 1', phone: '503-555-0102', hired: '2020-06-01', hours: 40 },
      { id: 3, name: 'Jose Lopez', role: 'Journeyman', payGroup: 'Journeyman Bricklayer', rate: 49.60, status: 'Active', union: 'BAC Local 1', phone: '503-555-0103', hired: '2018-01-10', hours: 45 },
      { id: 4, name: 'David Smith', role: 'Journeyman', payGroup: 'Journeyman Bricklayer', rate: 49.60, status: 'Active', union: 'BAC Local 1', phone: '503-555-0104', hired: '2021-04-20', hours: 40 },
      { id: 5, name: 'Roberto Garcia', role: '5th Period Apprentice', payGroup: '5th Period Apprentice Bricklayer', rate: 39.68, status: 'Active', union: 'BAC Local 1', phone: '503-555-0105', hired: '2022-02-01', hours: 40, apprenticeHours: 4200 },
      { id: 6, name: 'Miguel Hernandez', role: '3rd Period Apprentice', payGroup: '3rd Period Apprentice Bricklayer', rate: 29.76, status: 'Active', union: 'BAC Local 1', phone: '503-555-0106', hired: '2023-06-15', hours: 38, apprenticeHours: 2400 },
    ];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Employees</h1>
            <p className="text-slate-500">47 active employees</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-xl font-medium text-slate-700 hover:bg-slate-200">
              <Upload size={18} />
              Import
            </button>
            <button 
              onClick={() => { setModalType('employee'); setShowAddModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 rounded-xl font-medium text-white hover:bg-orange-600"
            >
              <Plus size={18} />
              Add Employee
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input type="text" placeholder="Search employees..." className="w-full pl-10 pr-4 py-2 rounded-xl bg-white border border-slate-200 text-sm" />
          </div>
          <select className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm">
            <option>All Roles</option>
            <option>Foreman</option>
            <option>Journeyman</option>
            <option>Apprentice</option>
          </select>
          <select className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm">
            <option>All Status</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>
        </div>

        {/* Employee List */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Employee</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Pay Group</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Rate</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">This Week</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Status</th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.map((emp) => (
                <tr key={emp.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedEmployee(emp)}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white text-sm font-bold">
                        {emp.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{emp.name}</p>
                        <p className="text-sm text-slate-500">{emp.union}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-800">{emp.payGroup}</span>
                      {emp.apprenticeHours && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                          {emp.apprenticeHours.toLocaleString()} hrs
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-slate-800">${emp.rate.toFixed(2)}/hr</td>
                  <td className="px-6 py-4 text-slate-600">{emp.hours} hrs</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                      {emp.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button className="p-2 hover:bg-slate-100 rounded-lg">
                      <MoreHorizontal size={18} className="text-slate-400" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Employee Detail Slide-over */}
        {selectedEmployee && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedEmployee(null)} />
            <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-200 p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-800">Employee Details</h2>
                  <button onClick={() => setSelectedEmployee(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Profile */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white text-xl font-bold">
                    {selectedEmployee.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">{selectedEmployee.name}</h3>
                    <p className="text-slate-500">{selectedEmployee.role}</p>
                  </div>
                </div>

                {/* Pay Group - Editable */}
                <div className="p-4 rounded-xl bg-orange-50 border border-orange-200">
                  <label className="block text-sm font-medium text-orange-800 mb-2">Pay Group</label>
                  <select className="w-full px-4 py-2 rounded-lg bg-white border border-orange-300 text-slate-800">
                    <option>Journeyman Bricklayer - $49.60</option>
                    <option>Foreman Bricklayer (1-3) - $52.60</option>
                    <option selected={selectedEmployee.role === 'Foreman'}>Foreman Bricklayer (4+) - $54.10</option>
                    <option>5th Period Apprentice - $39.68</option>
                    <option>4th Period Apprentice - $34.72</option>
                  </select>
                  <p className="text-xs text-orange-600 mt-2">Change pay group for this pay period only, or update employee default</p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="font-medium text-slate-800">{selectedEmployee.phone}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500">Hire Date</p>
                    <p className="font-medium text-slate-800">{selectedEmployee.hired}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500">Union</p>
                    <p className="font-medium text-slate-800">{selectedEmployee.union}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500">Base Rate</p>
                    <p className="font-medium text-slate-800">${selectedEmployee.rate.toFixed(2)}/hr</p>
                  </div>
                </div>

                {/* Certifications */}
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">Certifications</h4>
                  <div className="space-y-2">
                    {['OSHA 30', 'First Aid/CPR', 'Scaffold Competent Person'].map((cert, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                        <div className="flex items-center gap-2">
                          <BadgeCheck size={16} className="text-emerald-600" />
                          <span className="text-sm text-slate-700">{cert}</span>
                        </div>
                        <span className="text-xs text-slate-500">Valid</span>
                      </div>
                    ))}
                    <button className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-600">
                      <Plus size={16} />
                      Add Certification
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // TIME TRACKING MODULE
  // ============================================
  const TimeModule = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Time Tracking</h1>
          <p className="text-slate-500">Week of January 20 - January 26, 2026</p>
        </div>
        <div className="flex items-center gap-3">
          <select className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm">
            <option>This Week</option>
            <option>Last Week</option>
            <option>Custom Range</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 rounded-xl font-medium text-white">
            <Plus size={18} />
            Add Time Entry
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <p className="text-sm text-slate-500">Total Hours</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">2,184</p>
          <p className="text-sm text-emerald-600 mt-1">+124 from last week</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <p className="text-sm text-slate-500">Regular Hours</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">1,960</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <p className="text-sm text-slate-500">Overtime (1.5x)</p>
          <p className="text-3xl font-bold text-amber-600 mt-1">184</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <p className="text-sm text-slate-500">Double Time</p>
          <p className="text-3xl font-bold text-red-600 mt-1">40</p>
        </div>
      </div>

      {/* Time Grid by Job */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Time by Job</h3>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100">By Job</button>
            <button className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50">By Employee</button>
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-3 text-sm font-semibold text-slate-600">Job / Employee</th>
              <th className="px-3 py-3 text-sm font-semibold text-slate-600 text-center">Mon</th>
              <th className="px-3 py-3 text-sm font-semibold text-slate-600 text-center">Tue</th>
              <th className="px-3 py-3 text-sm font-semibold text-slate-600 text-center">Wed</th>
              <th className="px-3 py-3 text-sm font-semibold text-slate-600 text-center">Thu</th>
              <th className="px-3 py-3 text-sm font-semibold text-slate-600 text-center">Fri</th>
              <th className="px-3 py-3 text-sm font-semibold text-slate-600 text-center">Sat</th>
              <th className="px-3 py-3 text-sm font-semibold text-slate-600 text-center">Total</th>
              <th className="px-3 py-3 text-sm font-semibold text-slate-600">Pay Group</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr className="bg-slate-50">
              <td colSpan="9" className="px-4 py-2 font-semibold text-slate-800">
                <div className="flex items-center gap-2">
                  <Building2 size={16} />
                  CCC Sports Complex
                </div>
              </td>
            </tr>
            {[
              { name: 'Mike Rodriguez', hours: [8, 8, 8, 8, 8, 0], payGroup: 'Foreman (4+)' },
              { name: 'Carlos Martinez', hours: [8, 8, 8, 8, 8, 4], payGroup: 'Journeyman' },
              { name: 'Jose Lopez', hours: [8, 10, 8, 8, 8, 0], payGroup: 'Journeyman' },
            ].map((emp, idx) => (
              <tr key={idx} className="hover:bg-slate-50">
                <td className="px-4 py-3 pl-8">
                  <span className="text-sm text-slate-700">{emp.name}</span>
                </td>
                {emp.hours.map((h, i) => (
                  <td key={i} className="px-3 py-3 text-center">
                    <input 
                      type="number" 
                      defaultValue={h} 
                      className={`w-12 text-center py-1 rounded-lg text-sm ${h > 8 ? 'bg-amber-50 text-amber-700' : h > 0 ? 'bg-slate-100' : 'bg-slate-50 text-slate-300'}`}
                    />
                  </td>
                ))}
                <td className="px-3 py-3 text-center font-semibold text-slate-800">
                  {emp.hours.reduce((a, b) => a + b, 0)}
                </td>
                <td className="px-3 py-3">
                  <select className="text-xs px-2 py-1 rounded bg-slate-100 border-0">
                    <option>Journeyman</option>
                    <option selected={emp.payGroup.includes('Foreman')}>Foreman (4+)</option>
                    <option>Foreman (1-3)</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================
  // CREDIT CARD / EXPENSES MODULE
  // ============================================
  const ExpensesModule = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Credit Cards & Expenses</h1>
          <p className="text-slate-500">Track receipts, sync with bank</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-xl font-medium text-slate-700">
            <Link2 size={18} />
            Connect Bank
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 rounded-xl font-medium text-white">
            <Camera size={18} />
            Scan Receipt
          </button>
        </div>
      </div>

      {/* Connected Accounts */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center text-white text-xs font-bold">VISA</div>
              <div>
                <p className="font-medium text-slate-800">Business Visa</p>
                <p className="text-sm text-slate-500">****4521</p>
              </div>
            </div>
            <span className="px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">Synced</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">This month</span>
            <span className="font-bold text-slate-800">$12,450.32</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">MC</div>
              <div>
                <p className="font-medium text-slate-800">Fleet Card</p>
                <p className="text-sm text-slate-500">****8834</p>
              </div>
            </div>
            <span className="px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">Synced</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">This month</span>
            <span className="font-bold text-slate-800">$3,847.00</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-slate-400">
          <div className="text-center">
            <Plus size={24} className="text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-600">Add Card</p>
          </div>
        </div>
      </div>

      {/* Receipt Upload Area */}
      <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center">
            <Scan size={36} />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-1">AI Receipt Scanner</h3>
            <p className="text-violet-200">Take a photo or upload - AI extracts vendor, amount, date, and suggests job allocation</p>
          </div>
          <button className="px-6 py-3 bg-white text-violet-600 rounded-xl font-semibold hover:shadow-lg">
            Upload Receipt
          </button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Recent Transactions</h3>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">5 need receipts</span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">3 uncategorized</span>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {[
            { vendor: 'Mutual Materials', amount: 2847.50, date: 'Today', job: 'CCC Sports Complex', status: 'matched', category: 'Materials' },
            { vendor: 'Shell Gas Station', amount: 127.45, date: 'Today', job: null, status: 'needs-receipt', category: 'Fuel' },
            { vendor: 'Home Depot', amount: 456.78, date: 'Yesterday', job: null, status: 'uncategorized', category: null },
            { vendor: 'Fastenal', amount: 234.99, date: 'Yesterday', job: 'Riverton Hospital', status: 'matched', category: 'Supplies' },
            { vendor: 'AMEX Travel', amount: 847.00, date: 'Jan 18', job: 'Sheridan Holiday Inn', status: 'matched', category: 'Travel' },
          ].map((txn, idx) => (
            <div key={idx} className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  txn.status === 'matched' ? 'bg-emerald-100' :
                  txn.status === 'needs-receipt' ? 'bg-amber-100' : 'bg-red-100'
                }`}>
                  {txn.status === 'matched' ? <CheckCircle2 size={18} className="text-emerald-600" /> :
                   txn.status === 'needs-receipt' ? <Receipt size={18} className="text-amber-600" /> :
                   <AlertCircle size={18} className="text-red-600" />}
                </div>
                <div>
                  <p className="font-medium text-slate-800">{txn.vendor}</p>
                  <p className="text-sm text-slate-500">{txn.date} {txn.job && `• ${txn.job}`}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {txn.category && (
                  <span className="px-3 py-1 rounded-full text-xs bg-slate-100 text-slate-600">{txn.category}</span>
                )}
                <span className="font-bold text-slate-800">${txn.amount.toFixed(2)}</span>
                <ChevronRight size={18} className="text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================
  // JOBS MODULE
  // ============================================
  const JobsModule = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Jobs</h1>
          <p className="text-slate-500">6 active jobs</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 rounded-xl font-medium text-white">
          <Plus size={18} />
          New Job
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {[
          { name: 'CCC Sports Complex', client: 'School District', contract: 890000, billed: 534000, status: 'Active', pw: true },
          { name: 'Riverton Hospital', client: 'Riverton Health', contract: 527500, billed: 301000, status: 'Active', pw: true },
          { name: 'Alliant', client: 'Alliant Corp', contract: 133500, billed: 93450, status: 'Active', pw: false },
          { name: 'Fred Meyer Fueling', client: 'Kroger', contract: 71700, billed: 35850, status: 'Active', pw: false },
          { name: 'Sheridan Holiday Inn', client: 'IHG Hotels', contract: 350500, billed: 175250, status: 'Active', pw: true },
          { name: 'Mountain View Apartments', client: 'Vista Dev', contract: 425000, billed: 0, status: 'Bidding', pw: false },
        ].map((job, idx) => (
          <div key={idx} className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-md cursor-pointer transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-800">{job.name}</h3>
                  {job.pw && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Prevailing Wage</span>
                  )}
                </div>
                <p className="text-sm text-slate-500">{job.client}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                job.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {job.status}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Contract</span>
                <span className="font-medium text-slate-800">{formatCurrency(job.contract)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Billed</span>
                <span className="font-medium text-slate-800">{formatCurrency(job.billed)}</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full"
                  style={{ width: `${(job.billed / job.contract) * 100}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 text-right">{Math.round((job.billed / job.contract) * 100)}% complete</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ============================================
  // COMPLIANCE MODULE
  // ============================================
  const ComplianceModule = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Compliance</h1>
          <p className="text-slate-500">Certified payroll, LCP Tracker, apprentice ratios</p>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="text-emerald-600" size={20} />
            </div>
            <div>
              <p className="font-bold text-slate-800">LCP Tracker</p>
              <p className="text-sm text-emerald-600">Up to date</p>
            </div>
          </div>
          <p className="text-sm text-slate-500">Last submission: Jan 17, 2026</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="font-bold text-slate-800">Apprentice Ratio</p>
              <p className="text-sm text-amber-600">1 job needs attention</p>
            </div>
          </div>
          <p className="text-sm text-slate-500">CCC Sports Complex at 18% (need 20%)</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <FileText className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="font-bold text-slate-800">Certified Payroll</p>
              <p className="text-sm text-blue-600">Ready for review</p>
            </div>
          </div>
          <p className="text-sm text-slate-500">3 reports for week ending 1/19</p>
        </div>
      </div>

      {/* Prevailing Wage Jobs */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-800">Prevailing Wage Jobs</h3>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Job</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Wage Determination</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Apprentice Ratio</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">LCP Status</th>
              <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              { job: 'CCC Sports Complex', wage: 'OR State PW 2025', ratio: '18%', ratioOk: false, lcp: 'Current' },
              { job: 'Riverton Hospital', wage: 'WY Davis-Bacon', ratio: '22%', ratioOk: true, lcp: 'Current' },
              { job: 'Sheridan Holiday Inn', wage: 'WY Davis-Bacon', ratio: '25%', ratioOk: true, lcp: 'Due Friday' },
            ].map((job, idx) => (
              <tr key={idx} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{job.job}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{job.wage}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${job.ratioOk ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {job.ratio}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${job.lcp === 'Current' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                    {job.lcp}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button className="px-3 py-1 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg">
                    Export to LCP
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================
  // SETTINGS MODULE
  // ============================================
  const SettingsModule = () => (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Settings</h1>

      <div className="grid grid-cols-3 gap-6">
        {/* Company */}
        <div className="col-span-2 space-y-4">
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-4">Company Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                <input type="text" defaultValue="Romero Masonry Inc" className="w-full px-4 py-2 rounded-xl bg-slate-100 border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">EIN</label>
                <input type="text" defaultValue="XX-XXXXXXX" className="w-full px-4 py-2 rounded-xl bg-slate-100 border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input type="text" defaultValue="(503) 555-0100" className="w-full px-4 py-2 rounded-xl bg-slate-100 border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input type="email" defaultValue="office@romeromasonry.com" className="w-full px-4 py-2 rounded-xl bg-slate-100 border border-slate-200" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-4">Integrations</h3>
            <div className="space-y-3">
              {[
                { name: 'Payroll Service', status: 'Not connected', icon: DollarSign, action: 'Connect' },
                { name: 'LCP Tracker', status: 'Connected', icon: Shield, action: 'Settings' },
                { name: 'Bank (Plaid)', status: 'Connected', icon: Landmark, action: 'Manage' },
                { name: 'Google Workspace', status: 'Connected', icon: Mail, action: 'Manage' },
              ].map((int, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                      <int.icon size={20} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{int.name}</p>
                      <p className={`text-sm ${int.status === 'Connected' ? 'text-emerald-600' : 'text-slate-500'}`}>{int.status}</p>
                    </div>
                  </div>
                  <button className={`px-4 py-2 rounded-xl text-sm font-medium ${
                    int.status === 'Connected' ? 'bg-slate-200 text-slate-700' : 'bg-orange-500 text-white'
                  }`}>
                    {int.action}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* AI Settings */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800">AI Providers</h3>
              <span className="px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">All connected</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { name: 'Gemini Flash 3.0', use: 'PDF Parsing, Tax Research', color: 'bg-blue-500' },
                { name: 'Claude Haiku', use: 'Validation, Cleanup', color: 'bg-orange-500' },
                { name: 'GPT-4.1 Mini', use: 'Backup', color: 'bg-emerald-500' },
              ].map((ai, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${ai.color}`} />
                    <span className="font-medium text-slate-800 text-sm">{ai.name}</span>
                  </div>
                  <p className="text-xs text-slate-500">{ai.use}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-4">Team Members</h3>
            <div className="space-y-3 mb-4">
              {[
                { name: 'Roberto S.', role: 'Owner', email: 'roberto@' },
                { name: 'Laurie S.', role: 'Admin', email: 'laurie@' },
                { name: 'Maria G.', role: 'Office', email: 'maria@' },
              ].map((member, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{member.name}</p>
                    <p className="text-xs text-slate-500">{member.role}</p>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-xl text-sm text-slate-600 hover:border-slate-400">
              <UserPlus size={16} />
              Invite Member
            </button>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-4">States Active In</h3>
            <div className="flex flex-wrap gap-2">
              {['Oregon', 'Wyoming', 'Washington', 'Montana', 'Idaho'].map((state) => (
                <span key={state} className="px-3 py-1 rounded-full text-sm bg-slate-100 text-slate-700">{state}</span>
              ))}
            </div>
            <button className="mt-3 text-sm text-orange-600 font-medium">+ Add State</button>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // SIMPLE PLACEHOLDER MODULES
  // ============================================
  const PlaceholderModule = ({ title, description }) => (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
      <p className="text-slate-500">{description}</p>
      <div className="bg-white rounded-2xl p-12 border border-slate-100 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Layers size={32} className="text-slate-400" />
        </div>
        <p className="text-slate-600">This module is ready to be built out.</p>
        <p className="text-sm text-slate-400 mt-2">Click around to see the navigation and structure.</p>
      </div>
    </div>
  );

  // ============================================
  // VOICE MODAL
  // ============================================
  const VoiceModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => setShowVoice(false)} />
      <div className="relative bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Mic size={40} className="text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">Listening...</h3>
          <p className="text-slate-500 mb-6">Try saying:</p>
          <div className="space-y-2 text-left">
            {[
              '"Log 8 hours for Mike on CCC Sports Complex"',
              '"Add change order to Riverton for $15,000"',
              '"What\'s the backlog?"',
            ].map((example, idx) => (
              <p key={idx} className="text-sm text-slate-600 p-3 rounded-xl bg-slate-50">
                {example}
              </p>
            ))}
          </div>
        </div>
        <button
          onClick={() => setShowVoice(false)}
          className="w-full mt-6 py-3 bg-slate-100 rounded-xl font-medium text-slate-700 hover:bg-slate-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  if (!isAuthenticated) {
    return <AuthScreen />;
  }

  const renderModule = () => {
    switch (currentModule) {
      case 'dashboard': return <DashboardModule />;
      case 'employees': return <EmployeesModule />;
      case 'time': return <TimeModule />;
      case 'expenses': return <ExpensesModule />;
      case 'jobs': return <JobsModule />;
      case 'compliance': return <ComplianceModule />;
      case 'settings': return <SettingsModule />;
      case 'billing': return <PlaceholderModule title="Billing & SOV" description="Create invoices, manage SOVs, generate AIA forms" />;
      case 'payroll': return <PlaceholderModule title="Payroll" description="Review time, export to payroll service, track submissions" />;
      case 'backlog': return <PlaceholderModule title="Backlog Pipeline" description="Visual backlog tracking, contract vs billed, pending COs" />;
      case 'changeorders': return <PlaceholderModule title="Change Orders" description="Create, track, and approve change orders" />;
      case 'production': return <PlaceholderModule title="Production Tracking" description="Daily logs, productivity metrics, crew performance" />;
      case 'paygroups': return <PlaceholderModule title="Pay Groups" description="Union rates, AI sheet import, tax engine" />;
      default: return <DashboardModule />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      
      <Sidebar />
      
      <div className="flex-1 flex flex-col">
        <Header />
        <main className="flex-1 p-6 overflow-y-auto">
          {renderModule()}
        </main>
      </div>

      {showVoice && <VoiceModal />}
    </div>
  );
}
